import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Cpu, Pause, Play } from 'lucide-react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  Pill,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePagePanel,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { SURFACE_PAGE_TITLE } from '@/shared/ui-class';
import { Commentable } from '@/shared/comments/Commentable';
import { ControlChart } from '@/versions/v1/modules/_viz';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { mergeCasesForThreading } from '@/versions/v1/shared/threads';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { usePersona } from '@/app/providers';
import { casesWithSession, fmeaReviewsWithSession, recommendedActionsWithSession } from './Scene.transform';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { useAnalysisRun } from '@/versions/v1/shared/useAnalysisRun';
import { useAnalysisReplay } from '@/versions/v1/shared/useAnalysisReplay';
import { useAgentTrace } from '@/versions/v1/shared/useAgentTrace';
import { seniorMetallurgistSeatHolder, useRole } from '@/versions/v1/shared/RoleContext';
import { fmeaActionApproval } from '@/versions/v1/shared/FmeaLearningEngine';
import { recommendationEarlyWarning, recommendationFromTrace } from './AgentInvestigation.transform';
import { fmeaCasePreview, fmeaCaseRead } from './FmeaLearning.transform';
import { AgentInvestigationSection } from './AgentInvestigation';
import { recordedRunForTrace } from './InvestigationRun.transform';
import { AnalysisDiagnosisCandidateRow } from './AnalysisRunDiagnosis';
import { casePageRead } from './CasePage.transform';
import { advisoryShowsMoney } from './MidStageBodies.transform';
import { FmeaLearningPanel } from './FmeaLearning';
import { ActorChip, PipelineStrip } from './PipelineStrip';
import { StopAction } from './StopAction';
import { stopActionRead } from './StopAction.transform';
import { HandoffRegion } from './HandoffRegion';
import { handoffConditionRead, handoffGateRead, handoffStopRead } from './HandoffRegion.transform';
import { analysisFrameAtClock, buildCaseStageTimeline, flowClockAtOffset, flowClockOffset, formatFlowClock, REPLAY_DURATION_MS, ZINC_REPLAY_END, ZINC_REPLAY_START } from './FlowLanding.transform';

const REPLAY_MAX_OFFSET = flowClockOffset(ZINC_REPLAY_END);

/** Balanced two-line split for a routing node label (the graph is ~340 user units wide). */
function splitNodeLabel(label: string, maxChars: number): [string, string] {
  if (label.length <= maxChars) return [label, ''];
  const middle = label.length / 2;
  let best = -1;
  for (let index = label.indexOf(' '); index >= 0; index = label.indexOf(' ', index + 1)) {
    if (best < 0 || Math.abs(index - middle) < Math.abs(best - middle)) best = index;
  }
  return best > 0 ? [label.slice(0, best), label.slice(best + 1)] : [label.slice(0, maxChars), label.slice(maxChars)];
}

const ROUTE_W = 346;
const ROUTE_H = 98;
const ROUTE_NODE_H = 28;

function CaseRoutingGraph({
  signalTag,
  controlPlan,
  controlPlanId,
  rows,
}: {
  signalTag: string;
  controlPlan: string;
  controlPlanId?: string;
  rows: ReturnType<typeof casePageRead>['diagnosisRows'];
}) {
  if (rows.length === 0) return null;
  const routedRows = rows.slice(0, 3);
  const centers = routedRows.map((_, index) => ROUTE_NODE_H / 2 + 1 + index * (ROUTE_NODE_H + 6));
  const middleY = ROUTE_H / 2;
  const signal = { x: 1, w: 80 };
  const plan = { x: 92, w: 94 };
  const cand = { x: 212, w: ROUTE_W - 213 };
  return (
    <svg
      className="block w-full"
      viewBox={`0 0 ${ROUTE_W} ${ROUTE_H}`}
      role="img"
      aria-label="Signal routes through its Control Plan row to ranked FMEA candidates"
      data-routing-candidate-order={routedRows.map((row) => row.causeId).join(',')}
    >
      <defs>
        <marker id="case-route-arrow" markerUnits="userSpaceOnUse" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L0,6 L5,3 z" fill="var(--ink-tertiary)" />
        </marker>
      </defs>
      <path
        d={`M ${signal.x + signal.w} ${middleY} L ${plan.x - 1} ${middleY}`}
        fill="none"
        stroke="var(--ink-tertiary)"
        strokeWidth={1.25}
        markerEnd="url(#case-route-arrow)"
      />
      {centers.map((center, index) => (
        <path
          key={`route-${routedRows[index]!.causeId}`}
          d={`M ${plan.x + plan.w} ${middleY} C ${plan.x + plan.w + 14} ${middleY}, ${cand.x - 14} ${center}, ${cand.x} ${center}`}
          fill="none"
          stroke={index === 0 ? 'var(--color-accent-500)' : 'var(--ink-tertiary)'}
          strokeOpacity={index === 0 ? 1 : 0.55}
          strokeWidth={index === 0 ? 1.75 : 1.25}
          data-route-fan={index + 1}
        />
      ))}
      <rect x={signal.x} y={middleY - 18} width={signal.w} height={36} rx="7" fill="var(--surface-2)" stroke="var(--glass-border-strong)" />
      <text x={signal.x + 6} y={middleY - 4} fill="var(--ink-tertiary)" fontSize="10" fontWeight="600" letterSpacing="0.04em">SIGNAL</text>
      <text x={signal.x + 6} y={middleY + 10} fill="var(--ink-primary)" fontSize="10.5" fontWeight="500">{signalTag}</text>
      <rect data-route-control-plan={controlPlanId} x={plan.x} y={middleY - 18} width={plan.w} height={36} rx="7" fill="var(--surface-2)" stroke="var(--glass-border-strong)" />
      <text x={plan.x + 6} y={middleY - 4} fill="var(--ink-tertiary)" fontSize="10" fontWeight="600" letterSpacing="0.04em">CONTROL PLAN</text>
      <text x={plan.x + 6} y={middleY + 10} fill="var(--ink-primary)" fontSize="10.5" fontWeight="500">{controlPlan.slice(0, 18)}</text>
      {routedRows.map((row, index) => {
        const center = centers[index]!;
        const winner = index === 0;
        const [firstLine, secondLine] = splitNodeLabel(`${index + 1}. ${row.causeLabel}`, 23);
        return (
          <g key={row.causeId} data-cause-id={row.causeId} data-cause-order={index + 1}>
            <rect
              x={cand.x}
              y={center - ROUTE_NODE_H / 2}
              width={cand.w}
              height={ROUTE_NODE_H}
              rx="7"
              fill={winner ? 'var(--accent-soft)' : 'var(--surface-2)'}
              stroke={winner ? 'var(--color-accent-500)' : 'var(--glass-border-strong)'}
              strokeWidth={winner ? 1.5 : 1}
            />
            <text x={cand.x + 7} y={secondLine ? center - 2 : center + 4} fill="var(--ink-primary)" fontSize="10.5" fontWeight={winner ? 600 : 500}>
              <tspan x={cand.x + 7} dy="0">{firstLine}</tspan>
              {secondLine ? <tspan x={cand.x + 7} dy="11.5">{secondLine}</tspan> : null}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Header chips at the chip step of the type ramp (DESIGN.md § Type ramp: 10.5px, 600, 0.05em). */
const HEADER_CHIP = 'px-1.5 text-[10px] font-semibold tracking-[0.05em]';

function ReplayClock({ cursor, onCursor }: { cursor: SeriesTime; onCursor: (next: SeriesTime) => void }) {
  const [playing, setPlaying] = useState(false);
  const offsetRef = useRef(flowClockOffset(cursor));
  const maxOffset = REPLAY_MAX_OFFSET;
  const offset = Math.min(maxOffset, flowClockOffset(cursor));
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);
  useEffect(() => {
    if (!playing) return undefined;
    const startOffset = offsetRef.current;
    const durationMs = Math.max(1, REPLAY_DURATION_MS * ((maxOffset - startOffset) / maxOffset));
    const startedAt = performance.now();
    let frame = 0;
    let lastStep = startOffset;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / durationMs);
      const rawOffset = startOffset + (maxOffset - startOffset) * progress;
      const nextStep = progress >= 1 ? maxOffset : Math.floor(rawOffset / 15) * 15;
      if (nextStep !== lastStep) {
        lastStep = nextStep;
        offsetRef.current = nextStep;
        onCursor(flowClockAtOffset(nextStep));
      }
      if (progress >= 1) {
        setPlaying(false);
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [playing, maxOffset, onCursor]);
  const toggle = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    if (cursor < ZINC_REPLAY_START || cursor >= ZINC_REPLAY_END) {
      offsetRef.current = 0;
      onCursor(ZINC_REPLAY_START);
    } else offsetRef.current = offset;
    setPlaying(true);
  };
  return (
    <div className="flex min-w-0 items-center gap-1.5 rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-1.5 py-1.5">
      <button type="button" aria-label={playing ? 'Pause replay' : 'Play replay'} onClick={toggle} className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-ink-primary hover:bg-[var(--glass-surface-strong)]">
        {playing ? <Pause className="size-3.5" aria-hidden="true" /> : <Play className="size-3.5" aria-hidden="true" />}
      </button>
      <input
        type="range"
        aria-label="Replay time"
        data-case-replay-scrub
        min={0}
        max={maxOffset}
        step={15}
        value={offset}
        onChange={(event) => {
          setPlaying(false);
          const nextOffset = Number(event.currentTarget.value);
          offsetRef.current = nextOffset;
          onCursor(flowClockAtOffset(nextOffset));
        }}
        className="h-1.5 w-12 cursor-pointer accent-[var(--color-accent-500)]"
      />
      <span className="whitespace-nowrap font-mono text-[10px] font-medium tabular-nums text-ink-secondary" data-replay-clock>
        {formatFlowClock(cursor)}
      </span>
    </div>
  );
}

export function CasePage() {
  const { projectId, caseId } = useParams<{ projectId: string; caseId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const runParam = searchParams.get('run') ?? undefined;
  const project = projectTileBySlug(projectId);
  const scenario = useScenario();
  const advisory = useAdvisory();
  const cases = useMemo(() => casesWithSession(scenario.state, advisory), [scenario.state, advisory]);
  const diagnosis = useMemo(() => diagnose(scenario.state, advisory), [scenario.state, advisory]);
  const actions = useMemo(() => recommendedActionsWithSession(advisory), [advisory]);
  const reviews = useMemo(() => fmeaReviewsWithSession(advisory), [advisory]);
  const caseItem = useMemo(
    () => mergeCasesForThreading(cases, diagnosis).find((row) => row.id === caseId),
    [cases, diagnosis, caseId],
  );
  const fixture = caseItem?.analysisRun?.fixture;
  const run = useAnalysisRun(fixture);
  const replay = useAnalysisReplay(fixture);
  const trace = useAgentTrace(caseItem?.analysisRun?.recordedAgent ? fixture : undefined);
  const { can } = useRole();
  const { persona } = usePersona();
  // #731: one module-wide money guard — the floor seats (Operator, Control
  // room) read no US$ on the header, the stepper, the investigation or the
  // stop pair.
  const showMoney = advisoryShowsMoney(persona);
  const stageTimeline = useMemo(
    () => buildCaseStageTimeline(CASES, actions, reviews, fixture && replay.status === 'ready' ? { [fixture]: replay.frames } : {}),
    [actions, reviews, replay.status === 'ready' ? replay.frames : undefined],
  );
  const frame = replay.status === 'ready'
    ? analysisFrameAtClock(replay.frames, scenario.state.cursor)
    : undefined;
  const result = frame?.result ?? (
    run.status === 'ready' && run.result.window.end.slice(0, 16) <= scenario.state.cursor
      ? run.result
      : undefined
  );
  const read = caseItem
    ? casePageRead({
        caseItem,
        result,
        signalResult: run.status === 'ready' ? run.result : undefined,
        slices: run.status === 'ready' ? run.slices : undefined,
        diagnosis,
        state: scenario.state,
        fmeaLearning: advisory.fmeaLearning,
        stageTimeline: stageTimeline.get(caseItem.id) ?? buildCaseStageTimeline([caseItem], actions, reviews, replay.status === 'ready' ? replay.frames : []).get(caseItem.id)!,
        driverTreeSeed: DRIVER_TREE_SEED,
        showMoney,
      })
    : undefined;
  const sourceFrom = (location.state as { from?: unknown } | null)?.from;
  const backTo = typeof sourceFrom === 'string'
    ? sourceFrom
    : `/projects/${projectId ?? 'alpha'}/advisory/cases`;
  // K15: the recorded run is derived from the committed trace and the case seed;
  // a `?run=` deep link lands the column on that run (Diagnosis-stage entry).
  const recordedRun = useMemo(
    () => (trace.status === 'ready' ? recordedRunForTrace(CASES, trace.trace) : null),
    [trace.status === 'ready' ? trace.trace.recorded_at : undefined],
  );
  const investigationRuns = useMemo(
    () => [
      ...advisory.investigationRuns,
      ...(recordedRun ? [recordedRun] : []),
    ],
    [advisory.investigationRuns, recordedRun],
  );

  useEffect(() => {
    if (!runParam) return;
    document.querySelector('[data-case-section="investigation"]')?.scrollIntoView({ block: 'nearest' });
  }, [runParam, trace.status]);

  useEffect(() => {
    document.title = caseItem ? `${caseItem.code ?? caseItem.title} · ${project?.name ?? 'OptiFlow'}` : 'Case · OptiFlow';
  }, [caseItem, project?.name]);

  if (!caseItem || !read) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Case not found" />
          <Link to={backTo} className="text-sm font-medium text-accent-700 hover:underline dark:text-accent-400">Back to Advisory</Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const planText = read.controlPlanLabel ?? 'Control Plan';
  const signalRole = result?.diagnosis?.candidates[0]?.control_plan?.signal_role;
  const signalTag = signalRole
    ? run.status === 'ready'
      ? run.slices.signal_map?.find((row) => row.signal_role === signalRole)?.source_tag ?? signalRole
      : signalRole
    : 'Signal';
  const caseFmeaMode = read.fmeaMode;
  const stopAction = stopActionRead(caseItem, result);
  // D19: once the diagnosis has landed on the case clock, the recommendation's
  // FMEA change shows as a draft; the action gate raises it and the Senior
  // Metallurgist seat approves it live.
  const diagnosisLanded = caseItem.diagnosedAt !== undefined && scenario.state.cursor >= caseItem.diagnosedAt;
  const recommendation = diagnosisLanded && trace.status === 'ready' ? recommendationFromTrace(trace.trace) : undefined;
  // W3d: a controlled-stop case reads as the equipment handoff (D16, comp Handoff A).
  // K13d: the FMEA draft belongs to the Action stage — before the case reaches it
  // (08 Jun 03:15), the row reads its live values with no diff, exactly as the
  // zinc row does. The stage is the shared `read.cells.action` the stepper reads;
  // the handoff lines keep their own `stopAction &&` guard.
  const actionReached = Boolean(read.cells.action.count);
  const fmeaRead = caseFmeaMode
    ? fmeaCaseRead({
        mode: caseFmeaMode,
        state: advisory.fmeaLearning,
        caseId: caseItem.id,
        preview: fmeaCasePreview({
          actionReached,
          excursionDate: caseItem.raisedAt,
          controlledStop: stopAction !== undefined,
          hasRecommendation: recommendation !== undefined,
          earlyWarning: recommendation ? recommendationEarlyWarning(recommendation) : undefined,
          // K13c: the case's own lesson is seeded, not typed on the glass.
          learnedCause: caseItem.learnedCause,
          learnedEarlyWarning: caseItem.learnedEarlyWarning,
        }),
        // The seat holder's persona value is also its picker label (GM, Senior metallurgist).
        seatHolderLabel: seniorMetallurgistSeatHolder(),
        canApprove: can('fmea:approve-proposal'),
        // K13d: the row's next-step line is the controlled stop's, and shows
        // while that stop's inspection is still ahead of the replay cursor.
        stopPlan: caseItem.stopPlan,
        cursor: scenario.state.cursor,
      })
    : null;
  const visibleDiagnosisRows = read.diagnosisRows.slice(0, 3);
  const topControlPlan = result?.diagnosis?.candidates[0]?.control_plan;
  const handoff = caseItem.stopPlan && topControlPlan?.band_high != null
    ? {
        condition: handoffConditionRead({
          state: scenario.state,
          signalTag,
          controlPlan: { bandHigh: topControlPlan.band_high, specHigh: topControlPlan.spec_high, unit: topControlPlan.unit },
          bandCrossingAt: read.bandCrossingAt,
        }),
        stop: stopAction && actionReached ? handoffStopRead(stopAction, scenario.state, showMoney) : null,
        gate: stopAction && actionReached ? handoffGateRead({ caseItem, stop: stopAction, result, failureMode: caseFmeaMode }) : null,
      }
    : null;

  return (
    <SurfacePageLayout width="full">
      <SurfacePageBody>
        <div className="space-y-3">
          <header className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-2" data-case-header>
            <div className="flex min-w-0 flex-1 items-center gap-x-2">
              <Link to={backTo} aria-label="Back to Advisory" title="Back to Advisory" className="-ml-1.5 inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-[var(--glass-surface-strong)] hover:text-ink-primary">
                <ArrowLeft className="size-4" aria-hidden="true" />
              </Link>
              <h1 className={cn(SURFACE_PAGE_TITLE, 'min-w-0 truncate')} title={read.header.title}>{read.header.title}</h1>
              {/* Code, severity and circuit stay on the title's line (comp L525): the title truncates before a pill wraps. */}
              <span className="flex shrink-0 items-center gap-x-2 whitespace-nowrap">
                <span className="font-mono text-xs tracking-wide text-muted">{read.header.code}</span>
                <Pill tone={caseItem.severity === 'high' ? 'danger' : caseItem.severity === 'med' ? 'warning' : 'default'} className={HEADER_CHIP}>{read.header.severity}</Pill>
                {read.header.circuit ? <Pill tone="default" className={HEADER_CHIP}>{read.header.circuit}</Pill> : null}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ReplayClock cursor={scenario.state.cursor} onCursor={scenario.setCursor} />
              <HelpAnchor doc="advisory/case-page" />
              {read.header.value ? (
                <div className="max-w-[24rem] border-l border-[var(--hairline)] pl-2.5 text-right">
                  {read.header.valueLabel ? (
                    <p className="text-[10px] font-medium uppercase leading-snug tracking-wide text-muted" data-case-value-label>{read.header.valueLabel}</p>
                  ) : null}
                  <p className="text-base font-semibold leading-tight tabular-nums text-ink-primary" data-case-value>{read.header.value}</p>
                  {read.header.valueComparison ? (
                    <p className="mt-0.5 text-[10px] leading-snug text-muted tabular-nums" data-case-value-comparison>{read.header.valueComparison}</p>
                  ) : null}
                  {read.header.valuePath ? (
                    <p className="mt-0.5 whitespace-nowrap text-[10px] leading-snug text-ink-secondary" data-case-value-path={read.header.valuePath.join('>')}>
                      {read.header.valuePath.map((label, index) => (
                        <span key={label}>
                          {index > 0 ? <span aria-hidden="true" className="px-1 text-muted">→</span> : null}
                          {index > 0 ? <span className="sr-only">to </span> : null}
                          {label}
                        </span>
                      ))}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </header>

          <Commentable name="AdvisoryCasePage.Stepper">
            <div data-case-current-stage={read.activeStage ?? 'none'}>
              <PipelineStrip activeStage={read.activeStage} cells={read.cells} onSelect={() => undefined} mode="stepper" />
            </div>
          </Commentable>

          {handoff ? (
            <HandoffRegion
              condition={handoff.condition}
              stop={handoff.stop}
              routing={read.diagnosisRows.length > 0 && read.cells.diagnosis.count ? (
                <Commentable name="AdvisoryCasePage.Routing">
                  <div className="space-y-2 border-t border-[var(--color-divider)] pt-3" data-case-section="routing">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted">Routing</p>
                      <ActorChip actor="Engine" />
                    </div>
                    <CaseRoutingGraph signalTag={signalTag} controlPlan={planText} controlPlanId={topControlPlan?.row_id} rows={read.diagnosisRows} />
                    <ol className="divide-y divide-[var(--color-divider)] text-xs tabular-nums" data-ranked-candidates>
                      {visibleDiagnosisRows.map((row, index) => (
                        <li key={row.causeId} className="flex items-baseline gap-2 py-1 first:pt-0 last:pb-0" data-candidate-row={row.causeId}>
                          <span className={cn('w-3 shrink-0 font-semibold', index === 0 ? 'text-accent-700 dark:text-accent-400' : 'text-muted')}>{index + 1}</span>
                          <span className={cn('min-w-0 flex-1', index === 0 ? 'font-semibold text-ink-primary' : 'text-ink-secondary')} data-candidate-label>{row.causeLabel}</span>
                          <span className="shrink-0 whitespace-nowrap text-muted">{row.probabilityLabel}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </Commentable>
              ) : null}
              handoff={stopAction && handoff.gate ? <StopAction key={caseItem.id} caseId={caseItem.id} read={stopAction} handoff={handoff.gate} /> : null}
            />
          ) : (
          <div id="walkthrough-case-columns" className="grid grid-cols-1 items-stretch gap-3 xl:grid-cols-3">
            <Commentable name="AdvisoryCasePage.Evidence">
              <section className="min-w-0" data-case-section="evidence">
                <SurfacePagePanel title="Evidence" className="h-full" contentClassName="space-y-3">
                  {read.chart ? (
                    <div data-case-control-chart>
                      <ControlChart
                        model={read.chart}
                        height={150}
                        hideMeanLabel
                        yDomain={read.chartYDomain ?? undefined}
                        showBothSpecLimits
                        unitLabelPosition="top"
                        specLimitLabels
                        pointMarkers="highlighted"
                        breakGaps
                        timeTicks="even"
                        tickLabelPx={10}
                        seriesTone="ink"
                        meanLine="dashed"
                      />
                      <div className="mt-1.5 flex flex-wrap gap-x-3.5 gap-y-1 text-[10px] leading-tight text-muted tabular-nums">
                        {read.chart.bandHigh !== undefined ? <span data-control-plan-band>Band high {read.chart.bandHigh} {read.chart.unit}</span> : null}
                        {read.chart.spec?.lo !== undefined ? (
                          <span className="inline-flex items-center gap-1.5" data-control-plan-limit="low">
                            <span aria-hidden="true" className="w-3.5 border-t border-dotted border-[var(--state-warning)]" />
                            Control Plan low {read.chart.spec.lo.toFixed(2)} {read.chart.unit}
                          </span>
                        ) : null}
                        {read.chart.spec?.hi !== undefined ? (
                          <span className="inline-flex items-center gap-1.5" data-control-plan-limit="high">
                            <span aria-hidden="true" className="w-3.5 border-t border-dotted border-[var(--state-warning)]" />
                            Control Plan high {read.chart.spec.hi.toFixed(2)} {read.chart.unit}
                          </span>
                        ) : null}
                        {read.chart.excursionRange ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span aria-hidden="true" className="h-2.5 w-3.5 rounded-sm bg-[var(--accent-soft)]" />
                            Excursion {read.chart.excursionRange.startAt.slice(11, 16)}–{read.chart.excursionRange.endAt.slice(11, 16)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  <dl className="divide-y divide-[var(--hairline)]">
                    {read.facts.map((fact) => (
                      <div key={fact.label} className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-4 py-2 first:pt-0 last:pb-0" data-case-fact={fact.label}>
                        <dt className="whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-muted">{fact.label}</dt>
                        <dd className="min-w-0 text-right">
                          <span className="block text-xs font-semibold tabular-nums text-ink-primary">{fact.value}</span>
                          <span className="block text-[10px] leading-snug text-muted tabular-nums">{fact.comparison}</span>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </SurfacePagePanel>
              </section>
            </Commentable>

            {read.diagnosisRows.length > 0 ? (
              <Commentable name="AdvisoryCasePage.Routing">
                <section className="min-w-0" data-case-section="routing">
                  <SurfacePagePanel
                    title="Routing"
                    className="h-full"
                    contentClassName="space-y-3"
                    actions={(
                      <Pill tone="default" className="gap-1 px-1.5 text-[10px]" data-actor="engine">
                        <Cpu className="size-3" aria-hidden="true" />
                        Engine
                      </Pill>
                    )}
                  >
                    <CaseRoutingGraph signalTag={signalTag} controlPlan={planText} controlPlanId={result?.diagnosis?.candidates[0]?.control_plan?.row_id} rows={read.diagnosisRows} />
                    <div data-ranked-candidates>
                      <div className="flex items-baseline justify-between border-b border-[var(--hairline)] pb-1 text-[10px] font-medium uppercase tracking-wide text-muted">
                        <span>Cause</span>
                        <span>Probability</span>
                      </div>
                      {visibleDiagnosisRows.map((row, index) => <AnalysisDiagnosisCandidateRow key={row.causeId} row={row} rank={index + 1} compact />)}
                    </div>
                  </SurfacePagePanel>
                </section>
              </Commentable>
            ) : null}

            {fixture && caseItem.analysisRun?.recordedAgent ? (
              <div id="walkthrough-case-action-gate" className={cn('min-w-0', caseItem.diagnosedAt && scenario.state.cursor < caseItem.diagnosedAt ? 'self-start' : undefined)} data-case-section="investigation">
                <AgentInvestigationSection
                  caseId={caseItem.id}
                  caseCode={caseItem.code}
                  fixture={fixture}
                  diagnosedAt={caseItem.diagnosedAt as SeriesTime | undefined}
                  controlPlanLabel={read.controlPlanLabel}
                  layout="case-page"
                  onApproved={advisory.recordActionApproval}
                  traceState={trace}
                  tarpLadders={run.status === 'ready' ? run.slices.tarp : undefined}
                  recovery={caseItem.recovery}
                  actionApproval={fmeaActionApproval(advisory.fmeaLearning, caseItem.id)}
                  runs={investigationRuns}
                  selectedRunId={runParam}
                  showMoney={showMoney}
                />
              </div>
            ) : null}
          </div>
          )}

          {caseFmeaMode && fmeaRead ? (
            <Commentable name="AdvisoryCasePage.LivingFMEA">
              <FmeaLearningPanel
                mode={caseFmeaMode}
                state={advisory.fmeaLearning}
                read={fmeaRead}
                onApprove={() => advisory.approveFmeaProposal(caseItem.id)}
              />
            </Commentable>
          ) : null}
          <span className="sr-only" data-demo-cursor={scenario.state.cursor} />
        </div>
      </SurfacePageBody>
    </SurfacePageLayout>
  );
}
